import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AutoComplete } from "@/components/ui/autocomplete";
import { useDebounceEffect } from "@/hooks/use-debounce-effect";
import { useErrorHandler } from "@/hooks/use-error-handler";
import { cn } from "@/lib/utils";

import Loqate from "./utils/Loqate";

export interface AddressSearchProps {
  locale: string;
  apiKey: string;
  countries?: string[];
  limit?: number;
  onSelect: (address: Address) => void;
  manualEntryText?: string;
  className?: string;
  debounce?: number;
  apiUrl?: string;
}
export interface Address {
  AdminAreaCode: string;
  AdminAreaName: string;
  Barcode: string;
  Block: string;
  BuildingName: string;
  BuildingNumber: string;
  City: string;
  Company: string;
  CountryIso2: string;
  CountryIso3: string;
  CountryIsoNumber: number;
  CountryName: string;
  DataLevel: string;
  Department: string;
  District: string;
  DomesticId: string;
  Field1: string;
  Field2: string;
  Field3: string;
  Field4: string;
  Field5: string;
  Field6: string;
  Field7: string;
  Field8: string;
  Field9: string;
  Field10: string;
  Field11: string;
  Field12: string;
  Field13: string;
  Field14: string;
  Field15: string;
  Field16: string;
  Field17: string;
  Field18: string;
  Field19: string;
  Field20: string;
  Id: string;
  Label: string;
  Language: string;
  LanguageAlternatives: string;
  Line1: string;
  Line2: string;
  Line3: string;
  Line4: string;
  Line5: string;
  Neighbourhood: string;
  POBoxNumber: string;
  PostalCode: string;
  Province: string;
  ProvinceCode: string;
  ProvinceName: string;
  SecondaryStreet: string;
  SortingNumber1: string;
  SortingNumber2: string;
  Street: string;
  SubBuilding: string;
  Type: string;
}

const loqateLanguage = (language: string): string => {
  const [languageCode] = language.replace("_", "-").split("-");
  return languageCode;
};

export interface Item {
  Id: string;
  Description: string;
  Type: "Address" | "Postcode" | "Residential" | "Street";
  Text: string;
  Highlight: string;
}

export const AddressSearch = (props: AddressSearchProps) => {
  const {
    locale,
    countries,
    onSelect,
    manualEntryText = "Enter Address Manually",
    limit,
    apiKey,
    className,
    debounce,
    apiUrl,
  } = props;
  const loqate = useMemo(() => Loqate.create(apiKey, apiUrl), [apiKey, apiUrl]);
  const { handleMutationError } = useErrorHandler();

  const [suggestions, setSuggestions] = useState<Item[]>([]);
  const [value, setValue] = useState("");
  const [selectedSuggestion, setSelectedSuggestion] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);

  async function find(text: string, containerId?: string): Promise<Item[]> {
    setLoading(true);
    try {
      const { Items } = await loqate.find({
        countries,
        limit,
        text,
        containerId,
        language: loqateLanguage(locale),
      });
      return Items ?? [];
    } finally {
      setLoading(false);
    }
  }

  async function selectSuggestion(selectedSuggestion: Item): Promise<void> {
    const { Type, Id } = selectedSuggestion;
    if (Type === "Address") {
      const { Items = [] } = await loqate.retrieve(Id);

      if (Items.length) {
        setSuggestions([]);
      }
      onSelect(Items[0] as unknown as Address);
      if (inputRef.current) {
        inputRef.current.blur();
      }
      setSelectedSuggestion("");
      return;
    }

    const items = await find(value, Id);
    setSuggestions(items);
  }

  useDebounceEffect(
    () => {
      if (value === "" && suggestions.length > 0) {
        setSuggestions([]);
        return;
      }

      if (selectedSuggestion) {
        return;
      }

      if (value) {
        find(value).then(setSuggestions).catch(handleMutationError);
      }
    },
    debounce,
    [value, selectedSuggestion]
  );

  const { t } = useTranslation();

  return (
    <div className={cn(className)} data-test-id="react-loqate">
      <AutoComplete
        label={t("paymentMethod_searchAddress")}
        selectedValue={selectedSuggestion}
        onSelectedValueChange={(id) => {
          setSelectedSuggestion(id);

          const selectedSuggestion = suggestions.find(
            (suggestion) => suggestion.Id === id
          );
          if (selectedSuggestion) {
            selectSuggestion(selectedSuggestion).catch(handleMutationError);
          }
        }}
        searchValue={value}
        onSearchValueChange={setValue}
        items={suggestions.map((suggestion) => ({
          value: suggestion.Id,
          label: `${suggestion.Text.trim()} ${suggestion.Description.trim()}`,
        }))}
        placeholder=""
        enableManualEntry={true}
        manualEntryText={manualEntryText}
        ref={inputRef}
        isLoading={loading}
      />
    </div>
  );
};
